/**
 * Navigation module for managing dropdown menus and skip-to-content functionality.
 * @namespace navigation
 */
const navigation = {
  /**
   * Initializes the navigation module.
   * @memberof navigation
   * @function init
   */
  init() {
    /**
     * Holds all dropdown elements.
     * @memberof navigation
     * @type {NodeList}
     */
    this.detailsElements = document.querySelectorAll('.main-nav__dropdown');
    this.tabElements = document.querySelectorAll('.main-nav__content .main-nav__tabs');
    this.tabContents = document.querySelectorAll('.main-nav__content .main-nav__tab-content');
    this._initSkipToContent();
    this._ensureSingleOpenDetails();
    this._mainDetailsHandler();
    this._calculateTabContentHeight();
  },

  /**
   * Initializes the skip-to-content functionality.
   * @memberof navigation
   * @private
   * @function _initSkipToContent
   */
  _initSkipToContent() {
    const skipToContentButton = document.querySelector('.skip-to-content');
    if (skipToContentButton) {
      skipToContentButton.addEventListener('click', () => {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.scrollIntoView({ behavior: 'smooth' });
          mainContent.setAttribute('tabindex', '-1');
          mainContent.focus({ preventScroll: true });
        }
      });
    }
  },

  /**
   * Calculates the position of dropdown content.
   * @memberof navigation
   * @private
   * @function _mainDetailsHandler
   */
  _mainDetailsHandler() {
    const calcDropdownContentPositionHandler = (details, otherDetails) => {
      const breakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navigation-mobile-breakpoint'), 10) || 1200;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      if (viewportWidth > breakpoint && details.open) {
        const detailsWidth = details.offsetWidth;
        const dropdownContent = details.querySelector('.main-nav__dropdown--content');
        const elementWidth = dropdownContent.offsetWidth;

        const dropdownRect = details.getBoundingClientRect();
        const direction = elementWidth / 2 < dropdownRect.left ? 'right' : 'left';

        if (elementWidth / 2 < dropdownRect.left && elementWidth / 2 < viewportWidth - dropdownRect.right) {
          dropdownContent.style.transform = `translateX(calc(-50% + ${detailsWidth / 2}px - 1rem))`;
          dropdownContent.style.left = 'auto';
        } else {
          dropdownContent.style.transform = 'unset';
          if(direction === 'right') {
            dropdownContent.style.right = '0';
          } else {
            dropdownContent.style.left = '0';
          }
        }
      }
    };

    const verticalSpaceHandler = (details) => {
      // check has dropdown content space to open up
      const dropdownContent = details.querySelector('.main-nav__dropdown--content');
      const dropdownContentRect = dropdownContent.getBoundingClientRect();
      const dropdownContentHeight = dropdownContentRect.height;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const space = viewportHeight - dropdownContentRect.top;

      if (space < dropdownContentHeight && details.open) {
        // toglle scroll class
        details.classList.add('main-nav__dropdown--scroll');
      } else {
        details.classList.remove('main-nav__dropdown--scroll');
      }
    };

    this.detailsElements.forEach(details => {
      const otherDetails = [...this.detailsElements].filter(item => item !== details);
      details.addEventListener('toggle', () => calcDropdownContentPositionHandler(details, otherDetails));
      details.addEventListener('toggle', () => verticalSpaceHandler(details, otherDetails));
    });
  },

  /**
   * Ensures only one dropdown is open at a time.
   * @memberof navigation
   * @private
   * @function _ensureSingleOpenDetails
   */
  _ensureSingleOpenDetails() {
    const toggleHandler = (details, otherDetails) => {
      
      // Firefox browser return offsetHeight as 0 for tab contents elements before element is rendered.
      // So, this fixes issue on Firefox browser by calculating height of tab content element after it is rendered. 
      this._calculateTabContentHeight();

      if (details.open) {
        otherDetails.forEach(otherDetailsItem => {
          if (otherDetailsItem !== details) {
            otherDetailsItem.open = false;
          }
        });
      }
    };

    this.detailsElements.forEach(details => {
      const otherDetails = [...this.detailsElements].filter(item => item !== details);
      details.addEventListener('toggle', () => toggleHandler(details, otherDetails));
    });
  },

  /**
   * Calculates the height of tab contents and
   * sets the height of tab elements to the max height of tab content.
   * @memberof navigation
   * @private
   * @function _calculateTabContentHeight
   */
  _calculateTabContentHeight() {
    let maxHeight = 0;
    this.tabContents.forEach(content => {
      if (content.offsetHeight > maxHeight) {
        maxHeight = content.offsetHeight + 46;
      }
    });

    this.tabElements.forEach(element => {
      element.style.height = `${maxHeight}px`;
      element.style.minHeight = `${maxHeight}px`;
    });
  }
};

export default navigation;
